import { useCustomToast } from '@/hooks/use-custom-toast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CreateTicketCommentValidationSchema,
  TCreateTicketCommentValidation,
  useCreateTicketCommentMutation,
} from '../../api/create-ticket-comment.api';
import { useUpdateTicketCommentMutation } from '../../api/update-ticket-comment.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { GET_LIST_TICKET_COMMENT_QUERY_KEY, useListTicketCommentQuery } from '../../api/get-list-ticket-comment.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useEditedTicketCommentStore, useTicketCommentActionsStore } from './ticket-comment.store';
import useAuthContext from '@/features/auth/_shared/contexts/auth.context';

/**
 * Custom hook for managing comment list scrolling behavior
 *
 * This hook handles:
 * - Auto-scrolling to the newest comment when comments list updates
 * - Managing refs for the scrollable container and last comment element
 * - Cleaning up pending scroll timeouts to prevent memory leaks
 *
 * @param commentsCount - The number of comments in the list (triggers scroll when it changes)
 * @returns Object containing refs for scrolling
 */
const useCommentScroll = (commentsCount: number) => {
  // Ref for the scrollable comments list container
  const commentsContainerRef = useRef<HTMLDivElement>(null);

  // Ref for the last comment element (used for scroll-to-bottom functionality)
  const lastCommentRef = useRef<HTMLDivElement>(null);

  // Track all active timeouts for cleanup (prevents memory leaks)
  const timeoutRefs = useRef<Array<NodeJS.Timeout>>([]);

  /**
   * Auto-scroll to newest comment when comments list updates
   * This provides a better UX by showing the latest comment automatically
   *
   * Uses setTimeout to ensure DOM has updated before scrolling
   * Tracks timeout in timeoutRefs for proper cleanup
   */
  useEffect(() => {
    if (commentsCount > 0 && commentsContainerRef.current) {
      // Use setTimeout to ensure DOM is fully updated
      const timeoutId = setTimeout(() => {
        // Scroll to bottom of comments container
        if (commentsContainerRef.current) {
          commentsContainerRef.current.scrollTo({
            top: commentsContainerRef.current.scrollHeight, // Scroll to bottom
            behavior: 'smooth', // Smooth scroll animation
          });
        }

        // Remove timeout from tracking array after execution
        timeoutRefs.current = timeoutRefs.current.filter((id) => id !== timeoutId);
      }, 100); // Small delay to ensure DOM update

      // Track timeout for cleanup
      timeoutRefs.current.push(timeoutId);
    }
  }, [commentsCount]);

  /**
   * Cleanup effect: Clear all pending timeouts on unmount
   * Prevents memory leaks from pending scroll operations
   */
  useEffect(() => {
    return () => {
      // Clear all pending timeouts to prevent memory leaks
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current = [];
    };
  }, []);

  return {
    commentsContainerRef,
    lastCommentRef,
  };
};

/**
 * Custom hook for managing ticket comment functionality
 *
 * This hook handles:
 * - Fetching and displaying ticket comments
 * - Creating new comments
 * - Updating existing comments
 * - Managing comment input state (display text vs formatted text with mention IDs)
 *
 * Note: Scrolling behavior is handled separately by useCommentScroll hook
 *
 * @param ticketId - The ID of the ticket to manage comments for
 * @returns Object containing state, refs, form, mutations, and handlers
 */
export const useTicketComment = (ticketId: string) => {
  // ===== EXTERNAL HOOKS & STORES =====
  // Get current authenticated user for checking if comment belongs to current user
  const { user } = useAuthContext();

  // Get the comment that's currently being edited (from global store)
  const editedTicketComment = useEditedTicketCommentStore();

  // Get store actions for updating comment count and loading state
  const { setCountTicketComment, setIsLoadingFetchCountTicketComment, setEditedTicketComment } =
    useTicketCommentActionsStore();

  // ===== LOCAL STATE =====
  // Display text: What the user sees in the textarea (e.g., "@John Doe")
  // This is the user-friendly format without mention IDs
  const [text, setText] = useState('');

  // Raw content: Formatted text with mention IDs for server submission (e.g., "@John Doe|userId123")
  // This format includes user IDs so the server can properly process mentions
  const [rawContent, setRawContent] = useState('');

  // ===== REFS =====
  // Ref for the comment input container (used for layout/positioning)
  const containerRef = useRef<HTMLDivElement>(null);

  // Track if a submission is currently in progress (prevents double submission)
  const isSubmittingRef = useRef(false);

  // Toast notification utility
  const showToast = useCustomToast();

  // ===== FORM SETUP =====
  // React Hook Form setup with Zod validation
  // Validates comment content and tracks form state
  const form = useForm<TCreateTicketCommentValidation>({
    resolver: zodResolver(CreateTicketCommentValidationSchema),
    mode: 'all', // Validate on every change for real-time feedback
  });

  // ===== DATA QUERIES =====
  /**
   * Fetch all comments for the current ticket
   * - Automatically refetches every 1 minute to get new comments
   * - Only runs when ticketId is provided
   * - Uses staleTime: 0 to always get fresh data
   */
  const { data: listTicketCommentData, isLoading: isLoadingListTicketCommentData } = useListTicketCommentQuery({
    params: { ticketId },
    queryConfig: {
      enabled: !!ticketId, // Only fetch if ticketId exists
      refetchInterval: 1000 * 60 * 1, // Auto-refetch every 1 minute for real-time updates
      staleTime: 0, // Consider data stale immediately (always fetch fresh)
      gcTime: 0, // Don't cache data (always get latest)
    },
  });

  /**
   * Process and sort comments by creation date (oldest first)
   * This ensures comments appear in chronological order
   */
  const listTicketCommentMapped = useMemo(() => {
    // Return empty array if no data or still loading
    if (!listTicketCommentData?.payload || isLoadingListTicketCommentData) return [];

    // Sort comments by createdAt timestamp (ascending = oldest first)
    return listTicketCommentData?.payload?.sort((a, b) => {
      if (a?.createdAt && b.createdAt) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return -1; // If dates are missing, keep original order
    });
  }, [listTicketCommentData, isLoadingListTicketCommentData]);

  // ===== SCROLLING =====
  // Use custom hook to handle comment list scrolling
  const { commentsContainerRef, lastCommentRef } = useCommentScroll(listTicketCommentMapped.length);

  // ===== MUTATIONS =====
  /**
   * Mutation for creating a new comment
   * Handles the API call and post-submission cleanup
   */
  const mutationCreateTicketComment = useCreateTicketCommentMutation({
    mutationConfig: {
      onError: (_error) => {
        // Reset submission flag to allow retry
        isSubmittingRef.current = false;

        // Extract error message from API response or use default
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error creating comment, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: () => {
        // Reset submission flag to allow new submissions
        isSubmittingRef.current = false;

        // Show success notification
        showToast({ message: 'Comment created successfully', type: 'success' });

        // Invalidate and refetch comments list to show the new comment
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKET_COMMENT_QUERY_KEY, { ticketId }] });

        // Clear input fields and reset form
        setText('');
        setRawContent('');
        form.reset();
      },
    },
  });

  /**
   * Mutation for updating an existing comment
   * Similar to create, but also clears the edit state
   */
  const mutationUpdateTicketComment = useUpdateTicketCommentMutation({
    mutationConfig: {
      onError: (_error) => {
        // Reset submission flag to allow retry
        isSubmittingRef.current = false;

        // Extract error message from API response or use default
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error updating comment, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: () => {
        // Reset submission flag to allow new submissions
        isSubmittingRef.current = false;

        // Show success notification
        showToast({ message: 'Comment updated successfully', type: 'success' });

        // Invalidate and refetch comments list to show the updated comment
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKET_COMMENT_QUERY_KEY, { ticketId }] });

        // Clear edit state and input fields
        setEditedTicketComment(null);
        setText('');
        setRawContent('');
        form.reset();
      },
    },
  });

  // ===== HELPER FUNCTIONS =====
  /**
   * Converts formatted content (with mention IDs) to display text (without IDs)
   *
   * Example:
   * Input:  "@John Doe|userId123 hello @Jane|userId456"
   * Output: "@John Doe hello @Jane"
   *
   * This is used when loading an existing comment for editing,
   * so users see the friendly format instead of IDs
   */
  const convertRawContentToDisplayText = (rawContent: string): string => {
    if (!rawContent) return '';
    // Regex matches: @name|userId and replaces with just @name
    return rawContent.replace(/@([^\|]+)\|([\w-]+)/g, '@$1');
  };

  // ===== CLEANUP EFFECT =====
  /**
   * Component lifecycle management
   * - Sets mounted flag on mount
   * - Prevents state updates after component unmounts
   * - Resets submission flag on unmount
   */
  useEffect(() => {
    return () => {
      isSubmittingRef.current = false; // Reset submission flag on unmount
    };
  }, []);

  // ===== EFFECTS =====
  /**
   * Update global comment count when comments list changes
   * This count is used elsewhere in the app (e.g., badge indicators)
   */
  useEffect(() => {
    if (listTicketCommentMapped) {
      setCountTicketComment(listTicketCommentMapped?.length || 0);
    }
  }, [listTicketCommentMapped, setCountTicketComment]);

  /**
   * Sync loading state to global store
   * Other components can check if comments are being fetched
   */
  useEffect(() => {
    if (isLoadingListTicketCommentData) {
      setIsLoadingFetchCountTicketComment(true);
    } else {
      setIsLoadingFetchCountTicketComment(false);
    }
  }, [isLoadingListTicketCommentData, setIsLoadingFetchCountTicketComment]);

  /**
   * Reset form and state when ticket changes
   * This ensures we don't show stale data when switching between tickets
   */
  useEffect(() => {
    if (ticketId) {
      // Clear all input fields
      setText('');
      setRawContent('');
      setEditedTicketComment(null); // Clear any edit state
      isSubmittingRef.current = false; // Reset submission flag when ticket changes

      // Reset form with new ticketId
      form.reset({
        ticketId,
        hasMention: false,
      });
    }
  }, [ticketId, setEditedTicketComment, form]);

  /**
   * Handle comment editing mode
   * When a comment is selected for editing:
   * - Convert formatted content (with IDs) to display text (without IDs)
   * - Populate both text and rawContent states
   * - Update form values
   *
   * When editing is cancelled:
   * - Clear all input fields
   * - Reset form to default state
   */
  useEffect(() => {
    // Sync edited ticket comment data to the form
    if (editedTicketComment) {
      // Convert server format (@name|userId) to display format (@name)
      const displayText = convertRawContentToDisplayText(editedTicketComment.content);

      // Set both display and raw content
      setText(displayText);
      setRawContent(editedTicketComment.content);

      // Update form with the comment data
      form.setValue('content', editedTicketComment.content);
      form.setValue('hasMention', editedTicketComment.hasMention || false);
    } else {
      // Clear input when not editing (user cancelled or submitted)
      setText('');
      setRawContent('');
      form.reset({
        ticketId,
        hasMention: false,
      });
    }
  }, [editedTicketComment, ticketId, form]);

  // ===== HANDLERS =====
  /**
   * Submit comment (create new or update existing)
   *
   * Flow:
   * 1. Check if a submission is already in progress (prevent double submission)
   * 2. Validate content is not empty
   * 3. Get formatted content (with mention IDs) from rawContent or form
   * 4. Check if we're editing an existing comment or creating new
   * 5. Call appropriate mutation with formatted content
   *
   * Uses rawContent (formatted with IDs) instead of text (display format)
   * because the server needs user IDs to process mentions correctly
   */
  const handleSubmitComment = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    const contentToSubmit = (rawContent || form.watch('content') || '').trim();
    if (!contentToSubmit) {
      return;
    }

    // Sync values into RHF before validation
    form.setValue('content', contentToSubmit, { shouldValidate: true });
    form.setValue('ticketId', ticketId, { shouldValidate: true });

    form.handleSubmit((data) => {
      // If we are here, Zod schema (including max 30000) passed
      isSubmittingRef.current = true;

      if (editedTicketComment) {
        mutationUpdateTicketComment.mutate({
          commentId: editedTicketComment.commentId,
          content: data.content,
          hasMention: data.hasMention,
        });
      } else {
        mutationCreateTicketComment.mutate({
          ticketId: data.ticketId,
          content: data.content,
          hasMention: data.hasMention,
        });
      }
    })();
  }, [form, ticketId, rawContent, editedTicketComment, mutationCreateTicketComment, mutationUpdateTicketComment]);

  /**
   * Cancel editing mode
   * Clears edit state and resets form to default
   */
  const handleCancelEdit = useCallback(() => {
    setEditedTicketComment(null); // Clear edit state
    setText(''); // Clear display text
    setRawContent(''); // Clear formatted text
    form.reset({
      ticketId,
      hasMention: false,
    });
  }, [setEditedTicketComment, ticketId, form]);

  /**
   * Handle display text changes from TextareaMention component
   *
   * This is called when user types in the textarea.
   * Only updates the display text (what user sees).
   * The formatted text (with IDs) is handled separately by handleChangeFormatted.
   *
   * @param value - The display text (e.g., "@John Doe")
   */
  const handleChangeComment = useCallback((value: string) => {
    setText(value);
  }, []);

  /**
   * Handle formatted text changes from TextareaMention component
   *
   * This is called by TextareaMention when it processes mentions.
   * It receives the formatted text with mention IDs (e.g., "@John Doe|userId123").
   *
   * Updates:
   * - rawContent: Stores formatted text for server submission
   * - form.content: Updates form validation
   * - form.hasMention: Flags if comment contains mentions
   *
   * @param formattedValue - The formatted text with mention IDs (e.g., "@John Doe|userId123")
   */
  const handleChangeFormatted = useCallback(
    (formattedValue: string) => {
      setRawContent(formattedValue); // Store formatted text for submission

      // Check if comment contains valid mentions (format: @name|userId)
      // This regex matches: @ followed by name, then | followed by userId
      // This ensures we only detect actual mentions, not just standalone '@' symbols
      // or incomplete mentions without the userId part
      const mentionPattern = /@[^|]+\|[^|]+/;
      const hasMention = mentionPattern.test(formattedValue);

      // Update form values
      form.setValue('content', formattedValue);
      form.setValue('hasMention', hasMention);
    },
    [form]
  );

  // ===== RETURN VALUES =====
  return {
    // State
    user, // Current authenticated user (for checking comment ownership)
    text, // Display text (what user sees in textarea)
    editedTicketComment, // Comment currently being edited (null if not editing)
    listTicketCommentMapped, // Sorted list of comments (oldest first)
    isLoadingListTicketCommentData, // Loading state for comments fetch

    // Refs
    containerRef, // Ref for comment input container
    commentsContainerRef, // Ref for scrollable comments list
    lastCommentRef, // Ref for last comment element (for scroll tracking)

    // Form
    form, // React Hook Form instance (for validation and form state)

    // Mutations
    mutationCreateTicketComment, // Mutation for creating new comments
    mutationUpdateTicketComment, // Mutation for updating existing comments

    // Handlers
    handleSubmitComment, // Submit comment (create or update)
    handleCancelEdit, // Cancel editing mode
    handleChangeComment, // Handle display text changes
    handleChangeFormatted, // Handle formatted text changes (with mention IDs)
  };
};
